/**
 * dog-context.js
 * 
 * This context is used to manage the state of the dogs and provide a way to fetch and update dogs.
 */

import React from "react";
import axios from 'axios';
import { parseISO, isFuture } from 'date-fns';
import { UserContext } from 'contexts/user/user-context';
import useUser from 'utils/use-user';


const defaultState = {
  dogs: null,
  options: null,
  error: null
};


export const DogsContext = React.createContext({
  ...defaultState,
  setDogs: () => {},
  updateDog: () => {},
  fetchDogs: () => {},
  getActiveDogs: () => [],
});


export const DogsProvider = ({ userId, children }) => {
  const requestedUser = useUser(userId, false);
  const { user: authUser } = React.useContext(UserContext);
  const user = userId
    ? requestedUser
    : authUser;
  const [state, setState] = React.useState(defaultState);


  // Helper to sort dogs consistently
  // Sort dogs by default, then by date retired, then by date started
  const sortDogs = (dogs) => {
    return dogs.sort((a, b) => {
      if (a.isDefault) {
        return -1;
      } else if (b.isDefault) {
        return 1;
      } else if (a.dateRetired && !isFuture(a.dateRetired) && !b.dateRetired) {
        return 1;
      } else if (!a.dateRetired && b.dateRetired && !isFuture(b.dateRetired)) {
        return -1;
      } else {
        return b.dateStarted - a.dateStarted;
      }
    });
  };


  // Parse dates in dog objects and return a new object with the dates parsed
  const parseDogDates = (dog) => {
    return {
      ...dog,
      dateBirth: dog.dateBirth ? parseISO(dog.dateBirth) : null,
      dateRetired: dog.dateRetired ? parseISO(dog.dateRetired) : null,
      dateStarted: dog.dateStarted ? parseISO(dog.dateStarted) : null,
    };
  };


  // Fetch dogs from API
  const fetchDogs = React.useCallback(async (userId) => {
    try {
      const params = userId ? { userId } : undefined;
      const response = await axios.get('/dogs', { params });
      if (response.status === 200) {
        const dogs = response.data.dogs.map(parseDogDates);
        const sortedDogs = sortDogs(dogs);
        setState(prev => ({
          ...prev,
          dogs: sortedDogs,
          options: response.data.options,
          error: null
        }));

        return sortedDogs;
      }
    } catch (error) {
      if (error.response && error.response.status === 403 && error.response.data) {
        setState(prev => ({
          ...prev,
          error: error.response.data
        }));
      }
    }
  }, []);


  // Update a single dog in the state and update user's dog counts if needed
  const updateDog = React.useCallback((updatedDog) => {
    setState(prev => {
      // if the dog is default, make sure there is only one default dog
      let newDogs = updatedDog.isDefault
        ? prev.dogs.map(d => ({ ...d, isDefault: false }))
        : [...prev.dogs];

      // if the dog is already in the array, update it
      if (newDogs.find(d => d.id === updatedDog.id)) {
        newDogs = newDogs.map(d => updatedDog.id === d.id ? updatedDog : d);
      } else {
        newDogs = [updatedDog, ...newDogs];
      }

      // sort the dogs
      const sortedDogs = sortDogs(newDogs);

      // sort the dogs before adding them to the state
      return {
        ...prev,
        dogs: sortedDogs
      };
    });
  }, []);


  // Set entire dogs array  
  const setDogs = React.useCallback((dogs) => {
    if (!dogs) return;
    
    const parsedDogs = dogs.map(parseDogDates);
    setState(prev => ({
      ...prev,
      dogs: sortDogs(parsedDogs)
    }));
  }, []);


  // Get valid dogs for a given date (active dogs that have started and not retired)
  const getActiveDogs = React.useCallback((dateValue = new Date()) => {
    const dateObj = typeof dateValue === 'string' ? parseISO(dateValue) : dateValue;
    return state.dogs?.filter(dog => 
      (dog.dateStarted) <= dateObj
      && (!dog.dateRetired || (dog.dateRetired) >= dateObj)) || [];
  }, [state.dogs]);


  // Initialize dogs on mount if user exists
  React.useEffect(() => {
    if (user) {
      fetchDogs(userId);
    }
  }, [userId, user, fetchDogs]);


  // reset context when auth user logs out
  React.useEffect(() => {
    if (!authUser) {
      setState(defaultState);
    }
  }, [authUser]);


  const value = React.useMemo(() => ({
    ...state,
    setDogs,
    updateDog,
    fetchDogs,
    getActiveDogs,
  }), [
    state,
    setDogs,
    updateDog,
    fetchDogs,
    getActiveDogs,
  ]);


  return (
    <DogsContext.Provider value={value}>
      {children}
    </DogsContext.Provider>
  );
};